import { useEffect, useState, useRef } from "react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Plus, TrashSimple, User, PencilSimple, PaperPlaneTilt } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { relTime } from "./../lib/labels";

const COLUMNS = [
  { key: "todo", label: "À faire", accent: "bg-slate-500" },
  { key: "doing", label: "En cours", accent: "bg-blue-500" },
  { key: "done", label: "Terminé", accent: "bg-emerald-500" },
];

const COLORS = [
  { key: "yellow", cls: "bg-yellow-100 border-yellow-300 dark:bg-yellow-900/40 dark:border-yellow-700", label: "Jaune" },
  { key: "pink",   cls: "bg-pink-100 border-pink-300 dark:bg-pink-900/40 dark:border-pink-700", label: "Rose" },
  { key: "blue",   cls: "bg-blue-100 border-blue-300 dark:bg-blue-900/40 dark:border-blue-700", label: "Bleu" },
  { key: "green",  cls: "bg-emerald-100 border-emerald-300 dark:bg-emerald-900/40 dark:border-emerald-700", label: "Vert" },
  { key: "purple", cls: "bg-violet-100 border-violet-300 dark:bg-violet-900/40 dark:border-violet-700", label: "Violet" },
];

const colorClass = (c) => (COLORS.find((x) => x.key === c) || COLORS[0]).cls;

export default function PostItBoard({ scope }) {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // {id?, title, content, color, status, target_user_id?}
  const [teammates, setTeammates] = useState([]);
  const dragId = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/postits", { params: { scope } });
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur de chargement");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [scope]);

  // Fetch teammates (admin + technicien) once — used when personal-view creator wants to
  // deposit a post-it on a colleague's private board.
  useEffect(() => {
    if (scope !== "personal") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/users", { params: { role: "technicien" } });
        const r2 = await api.get("/users", { params: { role: "admin" } });
        if (cancelled) return;
        const all = [...(r.data || []), ...(r2.data || [])].filter((u) => u.active !== false);
        setTeammates(all);
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [scope]);

  const openNew = () => setEditing({ title: "", content: "", color: "yellow", status: "todo", target_user_id: user?.id || "" });

  const save = async () => {
    if (!editing.title.trim()) { toast.error("Titre requis"); return; }
    try {
      if (editing.id) {
        await api.patch(`/postits/${editing.id}`, {
          title: editing.title, content: editing.content, color: editing.color, status: editing.status,
        });
        toast.success("Post-it modifié");
      } else {
        const targetId = scope === "personal" ? (editing.target_user_id || user?.id) : null;
        const isForOther = scope === "personal" && targetId && targetId !== user?.id;
        await api.post("/postits", {
          scope,
          title: editing.title,
          content: editing.content,
          color: editing.color,
          status: editing.status,
          ...(scope === "personal" && targetId && targetId !== user?.id ? { target_user_id: targetId } : {}),
        });
        if (isForOther) {
          const tName = teammates.find((t) => t.id === targetId)?.full_name || "collègue";
          toast.success(`Post-it envoyé sur le Kanban personnel de ${tName}`);
        } else {
          toast.success("Post-it créé");
        }
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur d'enregistrement");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce post-it ?")) return;
    try {
      await api.delete(`/postits/${id}`);
      toast.success("Post-it supprimé");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    }
  };

  const onDrop = async (status) => {
    const id = dragId.current; dragId.current = null;
    if (!id) return;
    const it = items.find((x) => x.id === id);
    if (!it || it.status === status) return;
    // Optimistic update
    setItems((arr) => arr.map((x) => (x.id === id ? { ...x, status } : x)));
    try {
      await api.patch(`/postits/${id}`, { status });
    } catch (e) {
      toast.error("Déplacement échoué");
      load();
    }
  };

  const cols = COLUMNS.map((c) => ({ ...c, items: items.filter((i) => i.status === c.key) }));

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          {scope === "common"
            ? "Post-its partagés visibles et modifiables par toute l'équipe support."
            : "Vos post-its privés — visibles uniquement par vous."}
        </p>
        <Button size="sm" onClick={openNew} data-testid="postit-new-btn">
          <Plus size={16} className="mr-1.5"/> Nouveau post-it
        </Button>
      </div>

      <div className="flex gap-4 overflow-x-auto pb-4">
        {cols.map((col) => (
          <div key={col.key}
            className="kanban-col shrink-0 rounded-lg bg-muted/30 border"
            onDragOver={(e) => e.preventDefault()}
            onDrop={() => onDrop(col.key)}
            data-testid={`postit-col-${col.key}`}>
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${col.accent}`}/>
                <div className="font-display font-semibold text-sm">{col.label}</div>
              </div>
              <span className="text-xs mono text-muted-foreground">{col.items.length}</span>
            </div>
            <div className="p-3 space-y-3 min-h-[240px]">
              {loading && col.items.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-6">Chargement…</div>
              )}
              {!loading && col.items.length === 0 && (
                <div className="text-xs text-muted-foreground text-center py-6">Aucun post-it</div>
              )}
              {col.items.map((p) => {
                const receivedFromOther = p.created_by_id && p.created_by_id !== p.owner_id;
                const fromMeToOther = scope === "personal" && p.created_by_id === user?.id && p.owner_id !== user?.id;
                return (
                <div key={p.id}
                  draggable
                  onDragStart={() => (dragId.current = p.id)}
                  onClick={() => setEditing({ ...p })}
                  className={`rounded-md border-2 shadow-sm hover:shadow-md cursor-pointer transition-shadow p-3 space-y-2 ${colorClass(p.color)}`}
                  data-testid={`postit-card-${p.id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="font-medium text-sm leading-snug flex-1 break-words">{p.title}</div>
                    <div className="flex gap-1 shrink-0">
                      <button onClick={(e) => { e.stopPropagation(); setEditing({ ...p }); }}
                        className="p-1 rounded hover:bg-black/10 text-muted-foreground hover:text-foreground"
                        title="Modifier" data-testid={`postit-edit-${p.id}`}>
                        <PencilSimple size={13}/>
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); remove(p.id); }}
                        className="p-1 rounded hover:bg-red-500/20 text-muted-foreground hover:text-destructive"
                        title="Supprimer" data-testid={`postit-delete-${p.id}`}>
                        <TrashSimple size={13}/>
                      </button>
                    </div>
                  </div>
                  {receivedFromOther && scope === "personal" && !fromMeToOther && (
                    <div data-testid={`postit-from-${p.id}`} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[0.65rem] font-semibold bg-blue-100 text-blue-800 border border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800">
                      <PaperPlaneTilt size={10} weight="fill"/> De {p.created_by_name || "collègue"}
                    </div>
                  )}
                  {p.content && (
                    <p className="text-xs text-foreground/80 whitespace-pre-wrap line-clamp-4 break-words">{p.content}</p>
                  )}
                  <div className="flex items-center justify-between text-[0.65rem] text-foreground/60 pt-1 border-t border-black/10">
                    {scope === "common" ? (
                      <span className="flex items-center gap-1 truncate max-w-[70%]">
                        <User size={10}/> {p.owner_id === user?.id ? "Moi" : (p.owner_name || "Inconnu")}
                      </span>
                    ) : <span/>}
                    <span className="tabular-nums">{relTime(p.updated_at || p.created_at)}</span>
                  </div>
                </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent data-testid="postit-dialog">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier le post-it" : "Nouveau post-it"}</DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div>
                <label className="text-xs font-medium text-muted-foreground">Titre</label>
                <Input value={editing.title} data-testid="postit-title-input"
                  onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                  placeholder="Ex: Rappeler M. Dupont"
                  autoFocus/>
              </div>
              <div>
                <label className="text-xs font-medium text-muted-foreground">Note (optionnel)</label>
                <Textarea rows={4} value={editing.content || ""} data-testid="postit-content-input"
                  onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                  placeholder="Détails, contexte…"/>
              </div>
              {scope === "personal" && !editing.id && teammates.length > 0 && (
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">
                    Destinataire — dépose sur le Kanban personnel de :
                  </label>
                  <Select value={editing.target_user_id || user?.id}
                    onValueChange={(v) => setEditing({ ...editing, target_user_id: v })}>
                    <SelectTrigger data-testid="postit-target-user"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value={user?.id}>Moi ({user?.full_name})</SelectItem>
                      {teammates.filter((t) => t.id !== user?.id).map((t) => (
                        <SelectItem key={t.id} value={t.id} data-testid={`postit-target-${t.id}`}>
                          {t.full_name} · {t.role}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {editing.target_user_id && editing.target_user_id !== user?.id && (
                    <p className="text-[0.7rem] text-amber-700 dark:text-amber-400 mt-1 flex items-center gap-1">
                      <PaperPlaneTilt size={11} weight="fill"/>
                      Ce post-it sera déposé chez votre collègue — vous ne le verrez pas sur votre propre Kanban.
                    </p>
                  )}
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">Couleur</label>
                  <div className="flex gap-1.5 flex-wrap">
                    {COLORS.map((c) => (
                      <button key={c.key} type="button"
                        onClick={() => setEditing({ ...editing, color: c.key })}
                        title={c.label} data-testid={`postit-color-${c.key}`}
                        className={`w-7 h-7 rounded-full border-2 ${c.cls} ${editing.color === c.key ? "ring-2 ring-offset-1 ring-foreground" : ""}`}/>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-muted-foreground block mb-1">Colonne</label>
                  <div className="flex gap-1">
                    {COLUMNS.map((c) => (
                      <button key={c.key} type="button"
                        onClick={() => setEditing({ ...editing, status: c.key })}
                        data-testid={`postit-status-${c.key}`}
                        className={`text-xs px-2 py-1.5 rounded border flex-1 ${editing.status === c.key ? "bg-primary text-primary-foreground border-primary" : "bg-background hover:bg-muted"}`}>
                        {c.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} data-testid="postit-save-btn">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
